import type React from 'react'
import { useState } from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'

import { Button, AlternateButton } from '@/components/DEPRECATED_UI/Button'
import SlideInSidebar from '@/components/DEPRECATED_UI/SlideInSidebar'
import TextInput from '@/components/DEPRECATED_UI/TextInput'
import { Container, ErrorContainer } from '@/components/Style/Common'
import { useEditRefereeMutation } from '@/lib/apollo/mutation/edit-referee'
import { getErrorReasonsFromGraphQLExtensions } from '@/lib/apollo/utils/getUpstreamErrors'
import vouchLogger from '@/lib/vouchLogger'

import { errorCodesToMessages, refereeTypeToUrl } from './utils'
import type { EditRefereeErrors } from './utils'

const ProgressWarning = styled.div`
  color: red;
  font-weight: bold;
  margin-top: 12px;
`

export type RefereeType = 'landlord' | 'employer' | 'accountant'
interface SubjectData {
  customerId: string
  subjectId: string
  name: string | undefined
  email: string | undefined
  phone: string | undefined
}

export interface Props {
  setOpen: (open: boolean) => void
  subjectData: SubjectData
  refereeType: RefereeType
}

const EditRefereePanel: React.FC<Props> = ({ setOpen, subjectData, refereeType }) => {
  const [name, setName] = useState<string>(subjectData.name ?? '')
  const [email, setEmail] = useState<string>(subjectData.email ?? '')
  const [phone, setPhone] = useState<string>(subjectData.phone ?? '')

  const [editReferee, { loading, error }] = useEditRefereeMutation()
  const history = useHistory()
  function handleSubmit(e?: React.FormEvent) {
    if (e) {
      e.preventDefault()
    }

    editReferee({
      variables: {
        customerId: subjectData.customerId,
        subjectId: subjectData.subjectId,
        email,
        name,
        phone,
      },
    })
      .then(res => {
        const newSubjectId = res.data?.editReferee
        if (newSubjectId) {
          history.push(`${refereeTypeToUrl[refereeType]}/${newSubjectId}`)
        }
      })
      .catch(() => {
        vouchLogger.error('Error editing referee', {
          customerId: subjectData.customerId,
          subjectId: subjectData.subjectId,
        })
      })
  }

  const hasChanges =
    name !== subjectData.name ||
    email !== subjectData.email ||
    phone !== subjectData.phone

  return (
    <SlideInSidebar
      onClose={() => setOpen(false)}
      header={
        <div>
          <h2>Update referee details</h2>
        </div>
      }
      content={
        <>
          <Container onSubmit={handleSubmit}>
            <div>
              <TextInput
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setName(e.target.value)
                }}
                value={name}
                label="Full name"
                required
              />
              <TextInput
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setEmail(e.target.value)
                }}
                value={email}
                label="Email"
                required
              />
              <TextInput
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setPhone(e.target.value)
                }}
                value={phone}
                label="Phone"
                required
              />
            </div>
          </Container>
        </>
      }
      footer={
        <>
          {error && getErrorReasonsFromGraphQLExtensions(error).length > 0 && (
            <>
              {getErrorReasonsFromGraphQLExtensions(error).map(
                (reason: EditRefereeErrors) => {
                  return (
                    <ErrorContainer>
                      {errorCodesToMessages[reason] ?? errorCodesToMessages.GenericError}
                    </ErrorContainer>
                  )
                },
              )}
            </>
          )}
          <ProgressWarning>
            <div>
              Please note: Any details already submitted by the referee will be deleted.
            </div>
            <div>
              Please inform the referee they will be required to submit all details again.
            </div>
          </ProgressWarning>
          <div>
            <AlternateButton onClick={() => setOpen(false)}>Cancel</AlternateButton>
            <Button disabled={loading || !hasChanges} onClick={() => handleSubmit()}>
              Confirm
            </Button>
          </div>
        </>
      }
    />
  )
}

export default EditRefereePanel
